import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import Moment from 'react-moment'
import {ProjectRequestApprovalClient} from '../../../infrastracture/api/ProjectRequestApprovalClient'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'

// Where the Approve link in the manager approval email lands (see
// SendProjectManagerApprovalCommand) — anonymous, no CRM login needed, the token in the URL is
// the only credential. Applies the decision itself, on mount, with no separate confirm step;
// this page only ever reports the outcome. Only "approve" is wired up from the email right now
// — a future reject link would reuse this same page at its own route, passing 'reject' instead.
export function ProjectRequestApprovalPage() {
  const params: any = useParams()
  const [state, setState] = useState<'loading' | 'done' | 'error'>('loading')
  const [result, setResult] = useState<any>(null)

  useEffect(() => {
    let cancelled = false

    ProjectRequestApprovalClient.Respond(params.token, 'approve')
      .then((response) => {
        if (cancelled) return
        setResult(response)
        setState('done')
      })
      .catch(() => {
        if (cancelled) return
        setState('error')
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderBody = () => {
    if (state === 'loading') {
      return (
        <>
          <div className='spinner-border text-primary mb-5' role='status'></div>
          <div className='text-gray-600 fs-5'>Recording your response...</div>
        </>
      )
    }

    const somethingWentWrong = (
      <>
        <h1 className='text-dark mb-3'>Something went wrong</h1>
        <div className='text-gray-600 fs-5'>
          We couldn't record your response just now. Please try the link from the email again in a
          moment.
        </div>
      </>
    )

    // A failed call (network, 4xx/5xx, unreadable body) — never read as a decision.
    if (state === 'error') return somethingWentWrong

    if (result?.outcome === 'NotFound') {
      return (
        <>
          <h1 className='text-dark mb-3'>Link not recognised</h1>
          <div className='text-gray-600 fs-5'>
            This link doesn't look right. Please use the Approve button from the email.
          </div>
        </>
      )
    }

    if (result?.outcome === 'Expired') {
      return (
        <>
          <h1 className='text-dark mb-3'>Link expired</h1>
          <div className='text-gray-600 fs-5'>
            This approval link for <strong>{result.projectName}</strong> has expired. Please ask the
            requester to raise the project again.
          </div>
        </>
      )
    }

    // Only Recorded/AlreadyDecided carry a decision, and only an explicit 'approve' or 'reject'
    // is shown as one — anything else is treated as a failure rather than guessed at.
    const isDecided = result?.outcome === 'Recorded' || result?.outcome === 'AlreadyDecided'
    const isApproved = result?.decision === 'approve'
    const isRejected = result?.decision === 'reject'
    if (!isDecided || (!isApproved && !isRejected)) return somethingWentWrong

    const decidedOn = result.decidedOn && (
      <>
        {' '}
        on{' '}
        <Moment utc local format='ddd DD MMM YYYY, h:mm a'>
          {result.decidedOn}
        </Moment>
      </>
    )

    return (
      <>
        <h1 className={`mb-3 ${isApproved ? 'text-success' : 'text-danger'}`}>
          {isApproved ? 'Request approved' : 'Request rejected'}
        </h1>
        <div className='text-gray-600 fs-5 mb-3'>
          {result.outcome === 'AlreadyDecided' ? (
            <>
              <strong>{result.projectName}</strong> was already{' '}
              {isApproved ? 'approved' : 'rejected'}
              {decidedOn}.
            </>
          ) : isApproved ? (
            <>
              <strong>{result.projectName}</strong> has now been submitted for consideration.
            </>
          ) : (
            <>
              <strong>{result.projectName}</strong> has been rejected.
            </>
          )}
        </div>
        <div className='text-muted fs-7 mt-5'>You can close this page now.</div>
      </>
    )
  }

  return (
    <div className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'>
      <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
        <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto text-center'>
          <img
            alt='Logo'
            className='h-150px logo mb-5'
            src={toAbsoluteUrl('/media/logos/daysee_logo.png')}
          />
          {renderBody()}
        </div>
      </div>
    </div>
  )
}
